/**
 * The City panel's Workers tab: a target stepper per task, slot pips per
 * building with the workers holding them, the leftover (Free) workers, the
 * work focus, pinned workers and visitors. Data from `view/cityWorkers.ts`;
 * every action is a command (setWorkTargets, assign, automate, setHome).
 */
import { type ReactNode, useRef } from 'react';
import type { Building, Command, EntityId } from '@/game/sim';
import {
  type AddIdle,
  adoptCommand,
  assignIdleCommand,
  type CityWorkers,
  currentTargets,
  type PipKind,
  type PlaceRow,
  releaseCommand,
  type SentTargets,
  setTargetsCommand,
  steppedTargets,
  type TaskRow,
  targetsTotal,
  type WorkerChip,
  withTarget,
} from '@/game/view/cityWorkers';
import { workTargetsCapText } from '@/game/view/reasons';
import { nextIdle } from '@/game/view/workerRoster';

const PIP_CLASS: Record<PipKind, string> = {
  auto: 'bg-accent',
  pinned: 'bg-warn',
  visiting: 'bg-visitor',
  free: 'bg-fill',
};

const PIP_TITLE: Record<PipKind, string> = {
  auto: 'Auto worker',
  pinned: 'Held on this task',
  visiting: 'Visiting from another city',
  free: 'Free slot',
};

function Pips({ pips }: { pips: readonly PipKind[] }) {
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {pips.map((kind, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: pips are positional and have no identity
          key={i}
          title={PIP_TITLE[kind]}
          className={`h-2 w-3.5 rounded-sm ${PIP_CLASS[kind]}`}
        />
      ))}
    </span>
  );
}

function Chip({
  chip,
  onSelect,
  onRelease,
}: {
  chip: WorkerChip;
  onSelect: () => void;
  onRelease: () => void;
}) {
  const kindText = chip.kind === 'visiting' ? ` from ${chip.home}` : '';
  return (
    <span className="chip inline-flex items-center gap-1 py-0 text-[11px] tabular-nums">
      <button
        type="button"
        onClick={onSelect}
        title={`Select ${chip.label}${kindText}`}
        className="transition hover:text-accent"
      >
        {chip.label}
        {chip.kind === 'pinned' && ' 📌'}
        {chip.kind === 'visiting' && <span className="ml-1 text-visitor">{chip.home}</span>}
      </button>
      {chip.kind === 'pinned' && (
        <button
          type="button"
          aria-label={`Release ${chip.label} to auto`}
          title="Release to auto: the worker picks its own work again"
          onClick={onRelease}
          className="text-fg-muted transition hover:text-fg"
        >
          ✕
        </button>
      )}
    </span>
  );
}

function PlaceLine({
  row,
  onSelect,
  onRelease,
  onAdd,
}: {
  row: PlaceRow;
  onSelect: (id: EntityId) => void;
  onRelease: (id: EntityId) => void;
  onAdd: (add: AddIdle, buildingId: EntityId) => void;
}) {
  const add = row.addIdle;
  const buildingId = row.buildingId;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-5 text-[11px]">
      <span className="w-32 shrink-0 truncate text-fg-muted" title={row.name}>
        {row.name}
      </span>
      {row.pips.length > 0 && <Pips pips={row.pips} />}
      <span className="shrink-0 tabular-nums text-fg-subtle">
        {row.percent !== undefined && `${row.percent}% · `}
        {row.slots && `${row.slots.used}/${row.slots.total}`}
      </span>
      {row.chips.map((chip) => (
        <Chip
          key={chip.unitId}
          chip={chip}
          onSelect={() => onSelect(chip.unitId)}
          onRelease={() => onRelease(chip.unitId)}
        />
      ))}
      {add && buildingId && (
        <button
          type="button"
          disabled={'reason' in add}
          title={'reason' in add ? add.reason : 'Pin the nearest idle worker of this city here'}
          onClick={() => onAdd(add, buildingId)}
          className="chip py-0 text-[11px] transition hover:bg-fill-hover"
        >
          + add idle worker
        </button>
      )}
    </div>
  );
}

function TaskBlock({
  row,
  target,
  atCap,
  cap,
  onStep,
  ...lines
}: {
  row: TaskRow;
  /** The target to show: the one just sent while the sim catches up. */
  target: number;
  atCap: boolean;
  cap: number;
  onStep: (task: string, delta: 1 | -1) => void;
} & Omit<Parameters<typeof PlaceLine>[0], 'row'>) {
  return (
    <section className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium">
          {row.icon} {row.name}
        </span>
        <span className="tabular-nums text-fg-muted">
          {row.now} now{row.visiting > 0 && ` · ${row.visiting} visiting`}
        </span>
        <span className="flex-1" />
        <fieldset className="flex items-center gap-1" aria-label={`${row.name} target`}>
          <button
            type="button"
            aria-label={`Fewer ${row.name} workers`}
            disabled={target <= 0}
            onClick={() => onStep(row.task, -1)}
            className="chip w-6 justify-center py-0 transition hover:bg-fill-hover"
          >
            −
          </button>
          <span
            className="w-5 text-center tabular-nums"
            title="Workers this city keeps on the task; auto workers fill the rest"
          >
            {target}
          </span>
          <button
            type="button"
            aria-label={`More ${row.name} workers`}
            disabled={atCap}
            title={atCap ? workTargetsCapText(cap) : undefined}
            onClick={() => onStep(row.task, 1)}
            className="chip w-6 justify-center py-0 transition hover:bg-fill-hover"
          >
            +
          </button>
        </fieldset>
      </div>
      {row.places.map((place) => (
        <PlaceLine key={place.key} row={place} {...lines} />
      ))}
    </section>
  );
}

export function CityWorkersTab({
  city,
  data,
  focus,
  tick,
  selectedUnitIds,
  dispatch,
  onSelectUnits,
}: {
  city: Building;
  data: CityWorkers;
  /** The work focus control, shown under the Free row. */
  focus: ReactNode;
  /** The sim tick, to expire a sent targets map. */
  tick: number;
  /** The selected units: "Select idle" picks the idle worker after them. */
  selectedUnitIds: readonly EntityId[];
  dispatch: (c: Command) => void;
  onSelectUnits: (ids: string[]) => void;
}) {
  // Clicks faster than the sim answers build on the map just sent, not the stale one.
  const sent = useRef<SentTargets | null>(null);
  // Read at click time too: several clicks can land before the next render.
  const live = () => currentTargets(sent.current, city.id, city.workTargets, tick);
  const targets = live();
  const send = (next: Record<string, number>) => {
    sent.current = { cityId: city.id, targets: next, base: { ...city.workTargets }, tick };
    dispatch(setTargetsCommand(city.id, next));
  };
  const atCap = targetsTotal(targets) >= data.cap;
  const release = (id: EntityId) => dispatch(releaseCommand([id]));
  const step = (task: string, delta: 1 | -1) => {
    const next = steppedTargets(live(), task, delta, data.cap);
    if (next) send(next);
  };
  const idle = data.free.idleIds;
  const nextIdleId = nextIdle(idle, selectedUnitIds);
  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="flex items-baseline gap-2">
        <span className="font-medium tabular-nums">
          {data.own} / {data.cap} workers
        </span>
        <span className="text-fg-subtle">
          Targets keep that many on a task; auto workers fill the rest.
        </span>
        <span className="flex-1" />
        <button
          type="button"
          disabled={!nextIdleId}
          title={
            nextIdleId
              ? "Select this city's first idle worker (the . key then cycles every idle worker)"
              : 'No idle worker in this city'
          }
          onClick={() => nextIdleId && onSelectUnits([nextIdleId])}
          className="chip shrink-0 gap-1 py-0 transition hover:bg-fill-hover"
        >
          Select idle <kbd className="text-fg-subtle">.</kbd>
        </button>
      </div>

      {data.tasks.map((row) => (
        <TaskBlock
          key={row.task}
          row={row}
          target={targets?.[row.task] ?? 0}
          atCap={atCap}
          cap={data.cap}
          onStep={step}
          onSelect={(id) => onSelectUnits([id])}
          onRelease={release}
          onAdd={(add, buildingId) => {
            if ('unitId' in add) dispatch(assignIdleCommand(add.unitId, buildingId));
          }}
        />
      ))}

      <section className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">Free</span>
          <span className="chip py-0 text-[10px] text-accent">AUTO</span>
          <span className="tabular-nums text-fg-muted">{idle.length} now</span>
        </div>
        <p className="pl-5 text-[11px] text-fg-subtle">
          {data.free.text ? `${data.free.text}. ` : ''}Auto fills the rest by output per worker,
          using the work focus.
        </p>
        {data.away.length > 0 && (
          <p className="pl-5 text-[11px] text-visitor">
            Away: {data.away.map((a) => a.text).join(' · ')}
          </p>
        )}
        {data.warn && (
          <p className="flex items-center gap-2 pl-5 text-[11px] text-warn">
            <span>{data.warn.text}</span>
            <button
              type="button"
              onClick={() => {
                const w = data.warn;
                const next = w && withTarget(live(), w.task, w.target, data.cap);
                if (next) send(next);
              }}
              className="chip py-0 text-[11px] text-warn transition hover:bg-fill-hover"
            >
              Set
            </button>
          </p>
        )}
      </section>

      <section className="flex flex-col gap-1">
        <span className="font-medium">Work focus</span>
        <div className="flex">{focus}</div>
        <p className="text-[11px] text-fg-subtle">
          Applies to the auto workers only. Pinned workers ignore it.
        </p>
      </section>

      {data.held.length > 0 && (
        <section className="flex flex-col gap-1">
          <span className="font-medium">Held on a task 📌</span>
          {data.held.map((h) => (
            <div key={h.unitId} className="flex items-center gap-2 pl-5 text-[11px]">
              <button
                type="button"
                onClick={() => onSelectUnits([h.unitId])}
                className="w-12 shrink-0 text-left tabular-nums transition hover:text-accent"
              >
                {h.label}
              </button>
              <span className="min-w-0 flex-1 truncate text-fg-muted">{h.text}</span>
              <button
                type="button"
                aria-label={`Release ${h.label} to auto`}
                title="Release to auto: the worker picks its own work again"
                onClick={() => release(h.unitId)}
                className="chip py-0 transition hover:bg-fill-hover"
              >
                🔓
              </button>
            </div>
          ))}
        </section>
      )}

      {data.visitors.length > 0 && (
        <section className="flex flex-col gap-1">
          <span className="font-medium">Visiting</span>
          {data.visitors.map((v) => (
            <div key={v.unitId} className="flex items-center gap-2 pl-5 text-[11px]">
              <button
                type="button"
                onClick={() => onSelectUnits([v.unitId])}
                className="w-12 shrink-0 text-left tabular-nums transition hover:text-accent"
              >
                {v.label}
              </button>
              <span className="min-w-0 flex-1 truncate text-fg-muted">
                from <span className="text-visitor">{v.from}</span> → {v.where}
              </span>
              <button
                type="button"
                title="Make this its home city: it works for this city's targets from now on"
                onClick={() => dispatch(adoptCommand(v.unitId, city.id))}
                className="chip py-0 transition hover:bg-fill-hover"
              >
                Adopt
              </button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
